import React, { useEffect, useState } from "react";
import { MapPin, Phone, Search, User, ChevronDown, ChevronUp, X } from "lucide-react";

import styles from "./RightSidebar.module.css";

import EmptyProfileImage from '../../../../../images/navbar/empty-profile-image.png';
import { getBusinessProfileDataWithBID } from "../../../Services/getBusinessProfileDataWithBID";
import { getSearchMessages } from "../../../Services/getSearchMessages";
import StarRating from "../../../../StarRating/startRating";
import BusinessProfilePureCSS from '../../../../../profiles/BusinessProfilePureCSS';

// Helper functions remain the same
const formatAddress = (model) => {
    const addressParts = [
        model.streetNumber, model.streetName, model.suburb, model.myState, model.postCode
    ];
    return addressParts.filter(part => part).join(', ');
};

const formatReviewText = (count) => {
    if (!count) return '';
    return `(${count} review${count !== 1 ? 's' : ''})`;
};

function RightSidebar({ conversationSearchId, setChatSearching, activeConversation, business }) {
    const [businessProfile, setBusinessProfile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isBusinessProfileOpen, setIsBusinessProfileOpen] = useState(false);
    const [isBioExpanded, setIsBioExpanded] = useState(false);
    const [isSearchActive, setIsSearchActive] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [filteredMessages, setFilteredMessages] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchAttempted, setSearchAttempted] = useState(false);

    useEffect(() => {
        if (activeConversation && business?.businessId) {
            const fetchBusinessData = async () => {
                setLoading(true);
                setError(null);
                setBusinessProfile(null);
                setIsBioExpanded(false);
                setIsSearchActive(false);
                setFilteredMessages([]);
                setSearchText("");
                setSearchAttempted(false);

                try {
                    const response = await getBusinessProfileDataWithBID(business.businessId);
                    if (response.model) {
                        setBusinessProfile({
                            name: response.model.businessName,
                            profilePic: response.model.profilePic,
                            address: formatAddress(response.model),
                            phone: response.model.phone,
                            overallRating: response.model.overallRating,
                            reviewCount: response.model.reviewCount,
                            googleRating: response.model.googleRating,
                            googleRatingCount: response.model.googleRatingCount,
                            description: response.model.businessDescription,
                            bio: response.model.businessBio,
                        });
                    } else { throw new Error("Business data not found."); }
                } catch (err) {
                    console.error("Failed to fetch business profile:", err);
                    setError("Could not load business details.");
                } finally { setLoading(false); }
            };

            fetchBusinessData();
        }
    }, [activeConversation, business?.businessId]);

    const handleToggleSearch = () => {
        setIsSearchActive(!isSearchActive);
        setFilteredMessages([]);
        setSearchText("");
        setSearchAttempted(false);
    };

    const handleSearchSubmit = async (e) => {
        e.preventDefault();
        if (!searchText.trim()) return;

        setSearchLoading(true);
        setSearchAttempted(true);
        try {
            const results = await getSearchMessages(conversationSearchId, searchText);
            setFilteredMessages(results?.searchResponse || []);
        } catch (error) {
            console.error("Failed to fetch search results:", error);
            setFilteredMessages([]);
        } finally {
            setSearchLoading(false);
        }
    };

    const BIO_MAX_LENGTH = 120;
    const isBioLong = businessProfile?.bio && businessProfile.bio.length > BIO_MAX_LENGTH;
    
    const getBioText = () => {
        if (!businessProfile?.bio) return '';
        if (isBioLong && !isBioExpanded) {
            return `${businessProfile.bio.substring(0, BIO_MAX_LENGTH)}...`;
        }
        return businessProfile.bio;
    };

    if (!activeConversation) return null;

    return (
        <>
            <div className={styles.sidebarContainer}>
                <div className={styles.profileCard}>
                    {loading && <p className={styles.infoText}>Loading profile...</p>}
                    {error && <p className={styles.errorText}>{error}</p>}
                    
                    {businessProfile && (
                        <>
                            {/* --- UPDATED: Profile Header with Icon Buttons --- */}
                            <div className={styles.profileHeader}>
                                <div className={styles.headerActions}>
                                    <button 
                                        type="button"
                                        onClick={handleToggleSearch} 
                                        className={styles.headerActionButton} 
                                        title={isSearchActive ? "Close Search" : "Search Messages"}
                                    >
                                        {isSearchActive ? <X size={20} /> : <Search size={20} />}
                                    </button>
                                    <button 
                                        type="button"
                                        onClick={() => setIsBusinessProfileOpen(true)} 
                                        className={styles.headerActionButton} 
                                        title="View Profile"
                                    >
                                        <User size={20} />
                                    </button>
                                </div>

                                <img 
                                    src={businessProfile.profilePic || EmptyProfileImage} 
                                    alt={`${businessProfile.name} profile`} 
                                    className={styles.profileImage}
                                />
                                <h3 className={styles.businessName} title={businessProfile.name}>
                                    {businessProfile.name}
                                </h3>
                                {businessProfile.description && !isSearchActive && (
                                    <p className={styles.description}>"{businessProfile.description}"</p>
                                )}
                            </div>

                            {/* Search Panel and Profile Content sections remain unchanged */}
                            <div className={`${styles.searchPanel} ${isSearchActive ? styles.searchPanelActive : ''}`}>
                                <div className={styles.searchHeader}>
                                    <h4 className={styles.sectionTitle}>Search Conversation</h4>
                                    {/* The close button inside the panel is now redundant, but we can keep it as an extra option */}
                                    <button onClick={handleToggleSearch} className={styles.searchCloseButton}>
                                        <X size={18} />
                                    </button>
                                </div>
                                <form onSubmit={handleSearchSubmit}>
                                    <div className={styles.searchInputContainer}>
                                        <Search size={18} className={styles.searchInputIcon} />
                                        <input
                                            type="text"
                                            placeholder="Search messages..."
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            className={styles.searchInput}
                                        />
                                    </div>
                                </form>
                                <div className={styles.searchResultsList}>
                                    {searchLoading && <p className={styles.infoText}>Searching...</p>}
                                    {!searchLoading && searchAttempted && filteredMessages.length === 0 && (
                                        <p className={styles.infoText}>No messages found.</p>
                                    )}
                                    {filteredMessages.map((msg) => (
                                        <div 
                                            key={msg.messageId} 
                                            className={styles.searchResultItem}
                                            onClick={() => {
                                                setChatSearching(msg);
                                                handleToggleSearch();
                                            }}
                                        >
                                            <div className={styles.resultHeader}>
                                                <span className={styles.resultUser}>{msg.businessUserName || msg.homeUserName}</span>
                                                <span className={styles.resultDate}>{new Date(msg.messageTime).toLocaleDateString()}</span>
                                            </div>
                                            <p className={styles.resultMessage}>{msg.message}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className={`${styles.profileContent} ${isSearchActive ? styles.profileContentHidden : ''}`}>
                                {/* All the content here is unchanged */}
                                <div className={styles.ratingsSection}>
                                    <div className={styles.ratingItem}>
                                        <StarRating ratings={businessProfile.overallRating} />
                                        <span className={styles.ratingText}>
                                            {businessProfile.overallRating?.toFixed(1)} {formatReviewText(businessProfile.reviewCount)}
                                        </span>
                                    </div>
                                    {businessProfile.googleRating && businessProfile.googleRatingCount > 0 && (
                                        <div className={styles.ratingItem}>
                                            <span className={styles.googleRatingText}>
                                                Google Rating: {businessProfile.googleRating.toFixed(1)} {formatReviewText(businessProfile.googleRatingCount)}
                                            </span>
                                        </div>
                                    )}
                                </div>
                                
                                {businessProfile.bio && (
                                    <div className={styles.bioSection}>
                                        <h4 className={styles.sectionTitle}>About Us</h4>
                                        <p className={styles.bioText}>{getBioText()}</p>
                                        {isBioLong && (
                                            <button 
                                                className={styles.readMoreButton} 
                                                onClick={() => setIsBioExpanded(!isBioExpanded)}
                                            >
                                                {isBioExpanded ? 'Show Less' : 'Read More'}
                                                {isBioExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                            </button>
                                        )}
                                    </div>
                                )}

                                <div className={styles.contactSection}>
                                    <h4 className={styles.sectionTitle}>Contact Information</h4>
                                    <div className={styles.contactList}>
                                        {businessProfile.address && (<div className={styles.contactItem}><MapPin size={18} className={styles.contactIcon} /><span>{businessProfile.address}</span></div>)}
                                        {businessProfile.phone && (<div className={styles.contactItem}><Phone size={18} className={styles.contactIcon} /><span>{businessProfile.phone}</span></div>)}
                                    </div>
                                </div>
                            </div>

                            {/* --- REMOVED: Old Action Buttons Div --- */}
                        </>
                    )}
                </div>
            </div>
            
            <BusinessProfilePureCSS
                isOpen={isBusinessProfileOpen}
                onClose={() => setIsBusinessProfileOpen(false)}
                businessId={business?.businessId}
                useIDTYPE={"BID"}
                showMessageButton={false}
            />
        </>
    );
}

export default RightSidebar;